import React, { useContext, useMemo } from 'react'

import PlannedHotelCtaContent from './Hotel/PlannedHotelCtaContent'
import { ItemCardVariant } from './ItemCard'
import ItineraryItemCard from './ItineraryItemCard'
import { PlannedItemPriceContent } from './PlannedItemPriceContent'
import BookmarkLocation from '../Bookmark/BookmarkCardNew/BookmarkLocation'
import BedbankOfferPriceDetails from '../ItemOfferPrice/BedbankOfferPriceDetails'
import TripSummaryCardLoadingSkeleton from '../TripSummary/TripSummaryCardLoadingSkeleton'

import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import TextButton from 'components/Luxkit/Button/TextButton'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferTileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import Group from 'components/utils/Group'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
} from 'constants/offer'
import GeoContext from 'contexts/geoContext'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import getUrgencyLabels from 'lib/offer/getUrgencyLabels'
import { isLEHotel } from 'lib/offer/offerTypes'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import { VIEW_OFFER_COPY } from 'tripPlanner/config'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useOffer } from 'tripPlanner/hooks/api'
import useAccommodationItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useAccommodationItemConfirmationCodes'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import useAccommodationOfferAvailable from 'tripPlanner/hooks/useAccommodationOfferAvailable'
import { HotelItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatShortDateRange,
  getOfferImage,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { getAccommodationPriceAndLabel } from 'tripPlanner/utils/hotel'
import { findHotelPackage } from 'tripPlanner/utils/offer'

interface Props {
  item: HotelItem
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

function HotelItemCard({ item, onClickCard, onClickTripName, variant }: Props) {
  // The query type includes hotels and tours but this card is only used for hotels
  const { data: offer, isLoading } = useOffer(item.code)
  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit ?? false

  const { isTaxExclusiveRegion } = useContext(GeoContext)

  const { imgId, imgSrc } = getOfferImage(offer)

  const productTypeLabel = offer ? (
    <ProductTypeLabel productType={offer.productType} />
  ) : null

  let priceAreaContent: JSX.Element | null = null
  let ctaAreaContent: JSX.Element | null = null
  let urgencyLabels: JSX.Element | null = null

  const roomDescription = item.savedItemData?.roomTypeName

  const isOfferAvailable = useAccommodationOfferAvailable(
    offer,
    item.savedItemData,
    item.code,
  )

  const timeDescription = formatShortDateRange(item)
  const confirmation = useAccommodationItemConfirmationCodes(item)
  const { occupanciesText, numberOfRoomsText } =
    useAccommodationItemOccupancyDescription(item)
  const timeAndOccupancyText = nonNullable([timeDescription, occupanciesText])
    .filter((text) => text.length > 0)
    .join(', ')

  const { currentRegionCode } = useContext(GeoContext)
  const labels: App.OfferUrgencyLabel[] = useMemo(() => {
    if (!!offer && !!currentRegionCode) {
      return getUrgencyLabels(offer as App.Offer, currentRegionCode, false)
    } else {
      return []
    }
  }, [offer, currentRegionCode])

  if (isLoading) {
    return <TripSummaryCardLoadingSkeleton />
  }

  if (
    offer &&
    (offer.type === OFFER_TYPE_HOTEL ||
      offer.type === OFFER_TYPE_ALWAYS_ON ||
      offer.type === OFFER_TYPE_LAST_MINUTE) &&
    labels.length > 0
  ) {
    urgencyLabels = <OfferUrgencyLabels offer={offer} />
  }
  // Whole purpose of this series of conditionals is to set the 'bottomContent' correctly
  // If nothing gets set, it's a booking or custom item
  if (isLeBookmark(item) && offer && offer.parentType === 'hotel') {
    if (viewAsTemplate) {
      if (offer.type === 'bedbank_hotel') {
        const { checkIn, checkOut } = item.savedItemData
        priceAreaContent =
          checkIn && checkOut ? (
            <BedbankOfferPriceDetails
              offer={offer}
              roomId={item.subCode}
              checkIn={checkIn}
              checkOut={checkOut}
            />
          ) : null
      } else {
        priceAreaContent = (
          <LEOfferPriceDetails
            offer={offer as App.Offer}
            package={findHotelPackage(offer.packages, item)}
          />
        )
      }
      ctaAreaContent = (
        <Group direction="horizontal" gap={12}>
          <SaveTripItemButton item={item} />
          <TextButton kind="primary" nonInteractable>
            {VIEW_OFFER_COPY}
          </TextButton>
        </Group>
      )
    } else {
      const { discountPercent, currencyCode } = item.savedItemData

      const [price, priceDisplayType, priceLabel] =
        getAccommodationPriceAndLabel(
          offer,
          item.savedItemData,
          isTaxExclusiveRegion,
        )

      priceAreaContent = (
        <PlannedItemPriceContent
          discountPercent={discountPercent}
          price={price}
          priceLabel={priceLabel}
          currencyCode={currencyCode}
          isOfferAvailable={isOfferAvailable}
          priceDisplayType={priceDisplayType}
        />
      )
      ctaAreaContent = (
        <PlannedHotelCtaContent
          item={item}
          isOfferAvailable={isOfferAvailable}
          variant={variant}
        />
      )
    }
  }

  const placeId = 'id' in item.place ? item.place.id : undefined

  if (isLeBookmark(item) && offer) {
    const locationDescription = (
      <BookmarkLocation
        variant="single"
        startLocation={item.place.region}
        showIcon={false}
      />
    )
    let inclusions: App.TileInclusion[] = EmptyArray

    if (isLEHotel(offer)) {
      if (offer.lowestPricePackage?.inclusionsV2) {
        inclusions = offer.lowestPricePackage.inclusionsV2.map((inclusion) => ({
          text: inclusion.inclusion.name,
          isHighlighted: false,
          icon: inclusion.inclusion.category?.icon,
          isBonus: inclusion.style === 'bonus',
        }))
      }
      if (inclusions.length === 0 && offer.tileInclusions) {
        inclusions = offer.tileInclusions
      }
    }
    let rating: number | undefined
    let numberOfReviews: number | undefined
    if ('property' in offer && offer.property) {
      rating = offer.property.rating
      numberOfReviews = offer.property.reviewsTotal
    }
    const offerLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''
    return (
      <SavedOfferCard
        item={item}
        name={item.place.name}
        imageOverlayContent={productTypeLabel}
        imgSrc={imgSrc}
        imgId={imgId}
        locationDescription={locationDescription}
        descriptors={[timeAndOccupancyText, numberOfRoomsText, roomDescription]}
        placeId={placeId}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        urgencyLabels={urgencyLabels}
        onClick={() => {
          onClickCard(offerLink)
        }}
        offerLink={offerLink}
        isSoldOut={!isOfferAvailable}
        testId="saved-hotel-card"
        onClickTripName={onClickTripName}
        variant={variant}
        inclusions={
          inclusions.length > 0 && (
            <OfferTileInclusions
              heading=""
              offerTitle={item.place.name}
              tileInclusionsList={inclusions}
              maxInclusionsCount={2}
              offerURL={offerLink}
            />
          )
        }
        rating={rating}
        numberOfReviews={numberOfReviews}
      />
    )
  } else {
    // Custom Item Card OR Booking
    const myEscapesLink =
      canEdit && !viewAsTemplate ? buildMyEscapesOrderLink(item) : undefined
    return (
      <ItineraryItemCard
        item={item}
        name={item.place.name}
        imgSrc={imgSrc}
        imgId={imgId}
        locationDescription={item.place.region}
        placeId={placeId}
        descriptors={[timeDescription, numberOfRoomsText, roomDescription]}
        address={item.place.address}
        confirmationCode={confirmation.codeString}
        confirmationCodeLabel={confirmation.label}
        imageOverlayContent={productTypeLabel}
        onClickTripName={onClickTripName}
        variant={variant}
        onClick={myEscapesLink ? () => onClickCard(myEscapesLink) : undefined}
      />
    )
  }
}

export default HotelItemCard
